<template>
    <div class="canvas-main">
        <canvas ref="myCanvas" class="canvas" width="330" height="650"></canvas>
    </div>
</template>

<script>
import axios from 'axios';
import { fabric } from 'fabric';
import iconDel from './img/icon-del.png';

export default {
    name: 'AnnotationCanvas',
    props: {
        imgSrc: {
            // 待标注背景图片
            type: String,
            default: '',
        },
        imgZoom: {
            // 图片缩放比例
            type: Object,
            default: () => ({}),
        },
        annoList: {
            // 需要预渲染的标注数据
            type: Array,
            default: () => [],
        },
        toolActivity: {
            type: Object,
            default: () => ({}),
        },
        eventCanEdit: {
            type: Boolean,
            default: false,
        },
        showRight: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            zoomLevel: 1, // 缩放层级
            currentTaggingId: '', // 当前标注Id
            currentTaggingName: '', // 当前标注标签
            myCanvas: null,
            fabricCanvas: null,
            toRecord: false, // 是否要记录标注数据改变
            containerWidth: 0,
            containerHeight: 0,
            adapterRatio: 0,
            imageBlobUrl: null,
            state: {
                isScaling: false,
                imgPoint: { x: 0, y: 0 },
                realPoint: { x: 0, y: 0 },
                mouseFrom: { x: 0, y: 0 },
                mouseTo: { x: 0, y: 0 },
                rectPath: '', // 矩形绘制路径
                color: '#ED6f6f', // 画笔颜色
                drawWidth: 4, // 笔触宽度
                drawingObject: null, // 当前绘制对象
                drawType: 'rectangle',
                pointArray: [],
                line: {},
                lineArray: [],
                activeLine: null,
                activeShape: false,
                polygonMode: false,
                doDrawing: false, // 绘制状态
                currentStep: 'pre',
                rectangleObj: [],
            },
            rectList: new Map(),
        };
    },
    watch: {
        annoList: {
            handler(newAnnoList) {
                if (newAnnoList.length > 0 || this.imgSrc) {
                    this.$nextTick(() => {
                        this.init();
                    });
                }
            },
            deep: true,
        },
        imgSrc(newImgSrc) {
            if (newAnnoList.length > 0 || newImgSrc) {
                this.$nextTick(() => {
                    this.init();
                });
            }
        },
        imgZoom: {
            handler(newVal) {
                this.setZoom(newVal.type, newVal.num);
            },
            deep: true,
        },
        toolActivity: {
            handler(newValue) {
                this.$nextTick(() => {
                    this.refreshPainting(newValue);
                });
            },
            immediate: true,
            deep: true,
        },
    },
    mounted() {
        if (!this.imgSrc) return;
        this.init();
    },
    beforeDestroy() {
        this.cleanupBlobUrl();
    },
    methods: {
        // 提醒父组件数据改变
        hasChange() {
            this.$emit('hasChange');
        },

        init() {
            // 整体初始化
            this.initFabricControl();
        },

        setZoom(type, num) {
            // eslint-disable-next-line no-nested-ternary
            this.zoomLevel = type === 'zoom' ? 1 : type === 'zoomIn' ? this.zoomLevel + num : this.zoomLevel - num;

            // 获取当前视口的中心点
            const viewportCenter = this.fabricCanvas.getCenter();

            // 重置视口变换
            this.fabricCanvas.setViewportTransform([1, 0, 0, 1, 0, 0]);

            // 设置缩放级别
            this.fabricCanvas.setZoom(this.zoomLevel);

            // 计算缩放后的视口中心
            const newCenter = this.fabricCanvas.getCenter();

            // 计算需要调整的偏移量
            const deltaX = viewportCenter.left - newCenter.left;
            const deltaY = viewportCenter.top - newCenter.top;

            // 调整视口以保持居中
            this.fabricCanvas.relativePan(new fabric.Point(deltaX, deltaY));
        },

        // 重新加载事件
        refreshPainting(val) {
            this.changeDrawType(val.id === 0 ? 'rectangle' : 'polygn'); // 更改标注方式
            this.setCurrentTagging(val); // 设置当前标注的属性
        },

        // 加载标注相关数据
        initFabricControl() {
            fabric.Object.prototype.perPixelTargetFind = true;
            fabric.Object.prototype.targetFindTolerance = 0;
            fabric.Object.prototype.hasRotatingPoint = false;
            // 初始化缩放控件的属性
            fabric.Object.prototype.cornerColor = 'white';
            fabric.Object.prototype.cornerStyle = 'circle';
            fabric.Object.prototype.transparentCorners = false;
            // 删除按钮 没研究 使用原demo
            fabric.Object.prototype.controls.deleteControl = new fabric.Control({
                x: 0.5,
                y: -0.5,
                offsetY: 16,
                cursorStyle: 'pointer',
                mouseUpHandler: this.deleteObject,
                render: this.renderIcon,
                cornerSize: 14,
            });

            if (this.fabricCanvas === null) {
                // 控制背景不缩放不选中
                this.fabricCanvas = new fabric.Canvas(this.$refs.myCanvas, {
                    selection: false,
                });
            } else {
                this.fabricCanvas.clear();
            }

            this.loadImage(this.imgSrc).then((blobUrl) => {
                this.imageBlobUrl = blobUrl;
                // 加载待标注图片作为背景图片 根据容器宽度适应高度
                fabric.Image.fromURL(this.imageBlobUrl, (img) => {
                    if (!img || !img.width || !img.height) return;

                    // 获取画布的尺寸
                    const canvasWidth = this.fabricCanvas.getWidth() || 480;
                    this.containerWidth = canvasWidth;
                    const canvasHeight = this.fabricCanvas.getHeight() || 600;

                    this.containerHeight = canvasHeight;

                    // 计算图片的缩放比例以保持宽高比
                    const scaleX = canvasWidth / img.width;
                    const scaleY = canvasHeight / img.height;
                    const scale = Math.min(scaleX, scaleY); // 保持宽高比

                    // 计算图片居中后的位置
                    const left = canvasWidth / 2; // (canvasWidth - img.width * scale) / 2;
                    const top = canvasHeight / 2; // (canvasHeight - img.height * scale) / 2;

                    // 设置图片的位置和缩放
                    img.set({
                        // scaleX,
                        // scaleY,
                        left,
                        top,
                        originX: 'center', // 水平居中
                        originY: 'center', // 垂直居中
                    });
                    img.scaleToWidth(canvasWidth); // 让图片宽度等于 canvas 宽度，高度会相应缩放以保持比例
                    // 将图片设置为背景
                    this.fabricCanvas.setBackgroundImage(
                        img,
                        this.fabricCanvas.renderAll.bind(this.fabricCanvas),
                    );
                    this.drawPreTagging();
                }, {
                    crossOrigin: 'anonymous', // 启用 CORS
                });
            });

            // 监听Mouse按下事件
            this.fabricCanvas.on('mouse:down', this.mousedown);
            // 监听Mouse双击按下事件
            this.fabricCanvas.on('mouse:dblclick', this.mousedbldown);
            // 监听Mouse移动事件
            this.fabricCanvas.on('mouse:move', this.mousemove);

            // 监听Mouse滚轮缩放事件
            this.fabricCanvas.on('mouse:wheel', (opt) => {
                let delta = opt.e.deltaY;
                let zoom = this.fabricCanvas.getZoom();
                zoom *= 0.999 ** delta;
                if (zoom > 10) zoom = 10;
                if (zoom < 0.5) zoom = 0.5;
                const pointer = this.fabricCanvas.getPointer(opt.e);
                this.zoomLevel = zoom;
                const originX = pointer.x;
                const originY = pointer.y;

                this.fabricCanvas.setZoom(zoom);
                this.fabricCanvas.setViewportTransform([
                    zoom,
                    0,
                    0,
                    zoom,
                    originX - originX * zoom,
                    originY - originY * zoom,
                ]);

                opt.e.preventDefault();
                opt.e.stopPropagation();
            });

            // 重置
            this.reset();
        },

        // 加载图片
        loadImage(imageUrl) {
            return new Promise(async (resolve, reject) => {
                try {
                    // 使用 axios 加载图片
                    const response = await axios.get(imageUrl, { responseType: 'blob' });
                    // 创建一个临时的 URL
                    const blobUrl = URL.createObjectURL(response.data);
                    // resolve URL
                    resolve(blobUrl);
                } catch (error) {
                    console.error('图片加载失败:', error);
                    reject(error);
                }
            });
        },

        mousedbldown(event) {
            this.generatePolygon(0);
        },

        reset() {
            this.currentTaggingId = '';
            this.clearAll();
            this.state.polygonMode = false;
            this.state.drawType = 'rectangle';
        },

        // 预加载已有的标注数据
        drawPreTagging() {
            let preList = JSON.parse(JSON.stringify(this.annoList));

            preList.map((p) => {
                this.currentTaggingId = p.id;
                this.currentTaggingName = p.name;
                if (p.type === 1) {
                    p.sx1 *= this.adapterRatio;
                    p.sy1 *= this.adapterRatio;
                    p.sx2 *= this.adapterRatio;
                    p.sy2 *= this.adapterRatio;

                    this.state.mouseFrom.x = p.sx1;
                    this.state.mouseFrom.y = p.sy1;
                    this.state.mouseTo.x = p.sx2;
                    this.state.mouseTo.y = p.sy2;
                    this.generateRect(0);
                } else if (p.type === 2) {
                    try {
                        let temp = JSON.parse(p.pointJson);
                        temp = temp.map((t) => {
                            t.left = this.adapterRatio * t.x;
                            t.top = this.adapterRatio * t.y;
                            return t;
                        });
                        this.state.pointArray = temp;
                        this.generatePolygon(0);
                    } catch {}
                }
            });

            // 不对任何Canvas对象聚焦
            this.fabricCanvas.discardActiveObject();
            this.reset();
            // 开启记录
            this.toRecord = true;
        },

        // 更改当前标注类型
        changeDrawType(tp) {
            if (tp === 'polygn') {
                this.drawPolygon();
            } else {
                this.clearAll();
                this.state.polygonMode = false;
                this.state.drawType = 'rectangle';
            }
        },

        // 设置当前标注
        setCurrentTagging(val) {
            if (this.currentTaggingId === val.id) return;
            this.currentTaggingId = val.id;
            this.currentTaggingName = ''; // val.name
            let tagging = this.rectList.get(val.id);
            if (!tagging) return;
            this.fabricCanvas.setActiveObject(tagging);
            this.fabricCanvas.requestRenderAll();
        },

        drawPolygon() {
            this.state.drawType = 'polygn';
            this.state.polygonMode = true;
            // 这里画的多边形，由顶点与线组成
            this.state.pointArray = []; // 顶点集合
            this.state.lineArray = []; // 线集合
        },

        /**
         * 清除所有标注和标注状态
         */
        clearAll() {
            if (this.fabricCanvas) {
                // 标注元素已添加objectId 以此删除无关要素
                let objects = this.fabricCanvas.getObjects();
                if (objects && objects.length) {
                    // 开发时后端接口有id为0情况  此处无关紧要
                    objects.map((x) => {
                        if (!x.objectId && x.objectId !== 0) this.fabricCanvas.remove(x);
                    });
                }
                this.fabricCanvas.renderAll(); // 重新渲染Canvas
            }

            // 回到矩形标注的准备状态
            this.state.currentStep = 'pre';
            // 设置标注状态为否
            this.state.doDrawing = false;
            // 回到点位标注的准备状态
            this.resetPolygon();
        },

        resetPolygon() {
            this.state.activeLine = null;
            this.state.activeShape = null;
            this.state.pointArray = [];
            this.state.lineArray = [];
        },

        // 借鉴前人 不知此处的意义
        transformMouse(mouseX, mouseY) {
            return { x: mouseX / 1, y: mouseY / 1 };
        },

        // 判断鼠标是否落在已有选区 是否点位落在了已有标注数据上
        isRepeatClick(e) {
            if (!this.rectList || !this.rectList.size) return false;
            let isRepeat = false;
            // eslint-disable-next-line no-restricted-syntax
            for (let [_key, value] of this.rectList) {
                // 判断点位是否落在选区上
                let point = new fabric.Point(e.e.offsetX, e.e.offsetY);
                if (value) {
                    let result = value.containsPoint(point);
                    // 如果落在选区 告诉父组件定位原标注 重新设置当前激活的标注ID标签等属性
                    if (result && this.eventCanEdit) {
                        this.fabricCanvas.setActiveObject(value);
                        break;
                    }
                }
            }
            return isRepeat;
        },

        // 鼠标按下
        mousedown(e) {
            this.isRepeatClick(e);

            // 如果当前没有选中标注 (实际业务需求参看Readme 使用时请调整) 返回
            if (!this.currentTaggingId && this.currentTaggingId !== 0) return;
            // 如果已经有当前标注数据 返回
            if (this.rectList.has(this.currentTaggingId)) return;

            // 点位标注
            if (this.state.drawType !== 'rectangle') {
                this.fabricCanvas.skipTargetFind = false;
                try {
                    if (this.state.polygonMode) {
                        if (this.state.pointArray.length === 0) {
                            let isRepeat = this.isRepeatClick(e);
                            if (isRepeat) return;
                        }
                        this.addPoint(e);
                    }
                } catch (error) {
                    console.log(error);
                }
                // eslint-disable-next-line brace-style
            }
            // 矩形标注
            else {
                // 有鼠标移动时显示的矩形框 先删除
                if (this.state.drawingObject) {
                    this.fabricCanvas.remove(this.state.drawingObject);
                }

                // 准备阶段
                if (this.state.currentStep === 'pre') {
                    let isRepeat = this.isRepeatClick(e);
                    if (isRepeat) return;
                    let xy = e.pointer || this.transformMouse(e.e.offsetX, e.e.offsetY);
                    this.state.mouseFrom.x = xy.x;
                    this.state.mouseFrom.y = xy.y;
                    this.state.doDrawing = true;
                    this.state.currentStep = 'ing';
                }
                // 已点击 进行中阶段
                else if (this.state.currentStep === 'ing') {
                    let xy = e.pointer || this.transformMouse(e.e.offsetX, e.e.offsetY);
                    this.state.mouseTo.x = xy.x;
                    this.state.mouseTo.y = xy.y;
                    this.generateRect(0);
                }
            }
        },

        // 生成矩形标注
        generateRect(num) {
            let left = this.state.mouseTo.x < this.state.mouseFrom.x ? this.state.mouseTo.x : this.state.mouseFrom.x;
            let top = this.state.mouseTo.y < this.state.mouseFrom.y ? this.state.mouseTo.y : this.state.mouseFrom.y;
            let width = Math.abs(this.state.mouseTo.x - this.state.mouseFrom.x);
            let height = Math.abs(this.state.mouseTo.y - this.state.mouseFrom.y);

            if (width + left > this.containerWidth) {
                width = this.containerWidth - left;
            }

            if (height + top > this.containerHeight) {
                height = this.containerHeight - top;
            }

            this.state.doDrawing = false;

            let rect = new fabric.Rect({
                objectId: this.currentTaggingId,
                left,
                top,
                fill: 'rgba(255, 255, 255, 0.01)',
                width,
                height,
                objectCaching: false,
                stroke: this.currentTaggingId === 0 ? '#ED6f6f' : this.state.color,
                strokeWidth: this.state.drawWidth,
                selectable: false, // 设置此属性为 false 以禁止编辑
                lockRotation: false,
                lockMovementX: false,
                lockMovementY: false,
                zIndex: 2,
            });

            rect.on('moving', (options) => {
                this.fixUpMoveOverflow();
                this.state.rectangleObj = this.getAllObjects();
                this.setShapeBind(num, this.state.rectangleObj);
                this.clearAll();

                if (this.toRecord) this.hasChange();
                return false;
            });

            rect.on('scaling', (options) => {
                this.fixUpMoveOverflow();
                this.clearAll();

                if (this.toRecord) this.hasChange();
                return false;
            });

            rect.on('modified', (options) => {
                this.objectChange();
                this.state.rectangleObj = this.getAllObjects();
                this.setShapeBind(num, this.state.rectangleObj);
                if (this.toRecord) this.hasChange();
            });

            rect.on('added', (options) => {
                this.objectChange();
                if (this.toRecord) this.hasChange();
            });

            rect.setControlVisible('mtr', false);

            this.fabricCanvas.add(rect);
            this.fabricCanvas.setActiveObject(rect);
            this.rectList.set(this.currentTaggingId, rect);
            this.state.rectangleObj = this.getAllObjects();
            this.setShapeBind(num, this.state.rectangleObj);
            this.clearAll();
        },

        addPoint(e) {
            let random = Math.floor(Math.random() * 10000);
            let id = new Date().getTime() + random;
            let circle = new fabric.Circle({
                radius: 5,
                fill: '#ffffff',
                stroke: '#333333',
                strokeWidth: 0.5,
                left: (e.pointer.x || e.e.layerX) / this.fabricCanvas.getZoom(),
                top: (e.pointer.y || e.e.layerY) / this.fabricCanvas.getZoom(),
                selectable: false,
                hasBorders: false,
                hasControls: false,
                originX: 'center',
                originY: 'center',
                id,
                objectCaching: false,
            });

            if (this.state.pointArray.length == 0) {
                circle.set({
                    fill: '#00FFFF',
                });
            }

            let points = [
                (e.pointer.x || e.e.layerX) / this.fabricCanvas.getZoom(),
                (e.pointer.y || e.e.layerY) / this.fabricCanvas.getZoom(),
                (e.pointer.x || e.e.layerX) / this.fabricCanvas.getZoom(),
                (e.pointer.y || e.e.layerY) / this.fabricCanvas.getZoom(),
            ];

            this.state.line = new fabric.Line(points, {
                strokeWidth: 2,
                fill: '#999999',
                stroke: '#999999',
                class: 'line',
                originX: 'center',
                originY: 'center',
                selectable: false,
                hasBorders: false,
                hasControls: false,
                evented: false,
                objectCaching: false,
            });

            if (this.state.activeShape) {
                let pos = this.fabricCanvas.getPointer(e.e);
                let activeShapePoints = this.state.activeShape.get('points');
                activeShapePoints.push({
                    x: pos.x,
                    y: pos.y,
                });
                let polygon = new fabric.Polygon(activeShapePoints, {
                    stroke: '#333333',
                    strokeWidth: 1,
                    fill: '#cccccc',
                    opacity: 0.3,
                    selectable: false,
                    hasBorders: false,
                    hasControls: false,
                    evented: false,
                    objectCaching: false,
                });
                this.fabricCanvas.remove(this.state.activeShape);
                this.fabricCanvas.add(polygon);
                this.state.activeShape = polygon;
                this.fabricCanvas.renderAll();
            } else {
                let polyPoint = [
                    {
                        x: (e.pointer.x || e.e.layerX) / this.fabricCanvas.getZoom(),
                        y: (e.pointer.y || e.e.layerY) / this.fabricCanvas.getZoom(),
                    },
                ];
                let polygon = new fabric.Polygon(polyPoint, {
                    stroke: '#333333',
                    strokeWidth: 1,
                    fill: '#cccccc',
                    opacity: 0.3,
                    selectable: false,
                    hasBorders: false,
                    hasControls: false,
                    evented: false,
                    objectCaching: false,
                });
                this.state.activeShape = polygon;
                this.fabricCanvas.add(polygon);
            }
            this.state.activeLine = this.state.line;

            this.state.pointArray.push(circle);
            this.state.lineArray.push(this.state.line);
            this.fabricCanvas.add(this.state.line);
            this.fabricCanvas.add(circle);
        },

        clearPolygonLines() {
            let points = [];
            this.state.pointArray.forEach((point) => {
                points.push({
                    x: point.left,
                    y: point.top,
                });
                this.fabricCanvas.remove(point);
            });
            this.state.lineArray.forEach((line) => {
                this.fabricCanvas.remove(line);
            });
            this.fabricCanvas.remove(this.state.activeShape).remove(this.state.activeLine);
            return points;
        },

        // 绘制不规则四边形
        generatePolygon(num) {
            let points = this.clearPolygonLines();
            let textBoxObjectId = num === 0
                ? this.currentTaggingId
                : `${this.currentTaggingId}-${new Date().getTime().toString()}`;

            let polygon = new fabric.Polygon(this.sortPoints(points), {
                left: 100,
                top: 100,
                fill: 'rgba(255, 0, 0, 0.5)',
                stroke: 'black',
                strokeWidth: 2,
            });

            polygon.on('moving', () => {
                this.fixUpMoveOverflow();
                this.clearAll();
                if (this.toRecord) this.hasChange();
                return false;
            });

            polygon.on('scaling', () => {
                this.fixUpMoveOverflow();
                this.clearAll();
                if (this.toRecord) this.hasChange();
                return false;
            });

            polygon.on('modified', () => {
                this.state.rectangleObj = this.getAllObjects();
                this.setShapeBind(num, this.state.rectangleObj);
                this.objectChange();
                if (this.toRecord) this.hasChange();
            });

            polygon.on('added', () => {
                this.objectChange();
                if (this.toRecord) this.hasChange();
            });

            polygon.setControlVisible('mtr', false);

            this.fabricCanvas.add(polygon);
            this.rectList.set(textBoxObjectId, polygon);
            this.fabricCanvas.setActiveObject(polygon);

            this.state.rectangleObj = this.getAllObjects();
            this.setShapeBind(num, this.state.rectangleObj);
        },

        // 保存绘画参数
        setShapeBind(num, val) {
            let dataImg = null;
            let obj = {};
            this.$nextTick(() => {
                dataImg = this.toImage();
                obj = {
                    markJsonPath: {
                        tp: this.toolActivity.id,
                        ...val[0],
                    },
                    img: dataImg,
                };

                this.$emit('taggingChange', obj, num);
            });
        },

        // 修复上移溢出
        fixUpMoveOverflow() {
            let rect = this.fabricCanvas.getActiveObject();

            let left = rect.left;
            let top = rect.top;
            let width = rect.width * rect.scaleX;
            let height = rect.height * rect.scaleY;

            if (left < 0) {
                rect.set({ left: 0 });
                left = 0;
            }

            if (top < 0) {
                rect.set({ top: 0 });
                top = 0;
            }

            if (width + left > this.containerWidth) {
                if (width > this.containerWidth) {
                    width = this.containerWidth;
                }

                rect.set({ left: this.containerWidth - width, scaleX: 1, width });
            }

            if (height + top > this.containerHeight) {
                if (height > this.containerHeight) {
                    height = this.containerHeight;
                }
                // 如果矩形顶部触及边界，将其设置在边界上
                rect.set({ top: this.containerHeight - height, scaleY: 1, height });
            }
        },

        mousemove(e) {
            if (!this.state.doDrawing) {
                // 减少绘制频率
                return;
            }

            let xy = e.pointer || this.transformMouse(e.e.offsetX, e.e.offsetY);
            if (
                // eslint-disable-next-line no-constant-condition
                true
                || (xy.y >= 0 && xy.x <= this.containerWidth && xy.y >= 0 && xy.y <= this.containerHeight)
            ) {
                let realX = 0;
                let realY = 0;

                if (xy.x >= this.containerWidth) {
                    realX = this.containerWidth;
                } else {
                    if (xy.x < 0) {
                        realX = 0;
                    }
                    realX = xy.x;
                }

                if (xy.y >= this.containerHeight) {
                    realY = this.containerHeight;
                } else {
                    if (xy.y < 0) {
                        realY = 0;
                    }
                    realY = xy.y;
                }

                this.state.mouseTo.x = realX;
                this.state.mouseTo.y = realY;
                // 矩形
                if (this.state.drawType == 'rectangle') {
                    if (this.state.currentStep === 'ing') {
                        this.drawing(e);
                    }
                } else if (this.state.drawType !== 'rectangle') {
                    if (this.state.activeLine && this.state.activeLine.class == 'line') {
                        let pointer = this.fabricCanvas.getPointer(e.e);
                        this.state.activeLine.set({ x2: pointer.x, y2: pointer.y });

                        let points = this.state.activeShape.get('points');
                        points[this.state.pointArray.length] = {
                            x: pointer.x,
                            y: pointer.y,
                            zIndex: 1,
                        };
                        this.state.activeShape.set({
                            points,
                        });
                        this.fabricCanvas.renderAll();
                    }
                    this.fabricCanvas.renderAll();
                }
            }
        },

        drawing(_event) {
            if (this.state.drawingObject) {
                this.fabricCanvas.remove(this.state.drawingObject);
            }
            let canvasObject = null;

            let mouseFrom = {};
            let mouseTo = {};

            if (this.state.mouseFrom.x > this.state.mouseTo.x) {
                mouseFrom = this.state.mouseTo;
                mouseTo = this.state.mouseFrom;
            } else {
                mouseFrom = this.state.mouseFrom;
                mouseTo = this.state.mouseTo;
            }

            let left = this.state.mouseTo.x < this.state.mouseFrom.x ? this.state.mouseTo.x : this.state.mouseFrom.x;
            let top = this.state.mouseTo.y < this.state.mouseFrom.y ? this.state.mouseTo.y : this.state.mouseFrom.y;

            let path = `M ${mouseFrom.x} ${mouseFrom.y} L ${mouseTo.x} ${mouseFrom.y} L ${mouseTo.x} ${mouseTo.y} L ${mouseFrom.x} ${mouseTo.y} L ${mouseFrom.x} ${mouseFrom.y} z`;

            this.state.rectPath = path;
            canvasObject = new fabric.Path(path, {
                left,
                top,
                stroke: this.state.color,
                selectable: false,
                strokeWidth: this.state.drawWidth,
                fill: 'transparent',
                hasControls: false,
            });

            if (canvasObject) {
                this.fabricCanvas.add(canvasObject);
                this.state.drawingObject = canvasObject;
            }
        },

        // 删除按钮-图片
        renderIcon(ctx, left, top, styleOverride, fabricObject) {
            let size = 24;
            ctx.save();
            ctx.translate(left, top);
            ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));
            ctx.drawImage(iconDel, -size / 2, -size / 2, size, size);
            ctx.restore();
        },

        // 删除Object
        deleteObject(eventData, transform) {
            let target = this.fabricCanvas.getActiveObject();
            this.fabricCanvas.remove(target);
            let all = this.fabricCanvas.getObjects();
            let textBox = all.find((x) => x.objectId == target.objectId);
            if (textBox) {
                this.fabricCanvas.remove(textBox);
            }
            this.fabricCanvas.requestRenderAll();
            this.clearAll();
            this.rectList.delete(target.objectId);
            this.setShapeBind(1, {});
            this.hasChange();
        },

        // 处理标注标签的文字自动跟随对象的移动或缩放
        objectChange() {
            let obj = this.fabricCanvas.getActiveObject();
            if (!obj) return;
            let all = this.fabricCanvas.getObjects();

            let textBox = all.find((x) => x.objectId == obj.objectId && x.tp === 'text');

            if (textBox) {
                textBox.set({
                    left: obj.left,
                    top: obj.top + (obj.height * obj.scaleY) / 2 - 7,
                    width: obj.width * obj.scaleX,
                    heigth: obj.height * obj.scaleY,
                });
            }
        },

        // 坐标点排序
        sortPoints(aCoord) {
            let points = JSON.parse(JSON.stringify(aCoord));
            // (tl tr) (bl,br)
            const poiT = this.linesIntersection(
                points[0] || points.tl,
                points[1] || points.tr,
                points[2] || points.br,
                points[3] || points.bl,
            );
            // (tl br) (tr,bl)
            const poiB = this.linesIntersection(
                points[0] || points.tl,
                points[3] || points.bl,
                points[2] || points.br,
                points[1] || points.tr,
            );
            if (poiT?.x != -1) {
                // 第二个和第三个坐标互换
                this.changePoint(points[1] || points.tr, points[2] || points.br);
            } else if (poiB?.x != -1) {
                // 第一个和第四个坐标互换
                this.changePoint(points[0] || points.tl, points[3] || points.bl);
                // 第二个和第三个坐标互换
                this.changePoint(points[1] || points.tr, points[2] || points.br);
                // 第一个和第二个坐标互换
                this.changePoint(points[0] || points.tl, points[1] || points.tr);
            }
            return points;
        },

        changePoint(poi1, poi2) {
            let temp = { x: poi1.x, y: poi1.y };
            poi1.x = poi2.x;
            poi1.y = poi2.y;
            poi2.x = temp.x;
            poi2.y = temp.y;
        },

        linesIntersection(tl, tr, br, bl) {
            let tn1 = tr.y - tl.y;
            let ty1 = tl.x - tr.x;
            let tn2 = br.y - bl.y;
            let ty2 = bl.x - br.x;
            let denominator = tn1 * ty2 - ty1 * tn2;
            if (denominator == 0) {
                return { x: -1, y: -1 };
            }
            // eslint-disable-next-line camelcase
            let distCN2 = tn2 * bl.x + ty2 * bl.y;
            // eslint-disable-next-line camelcase
            let distAN2 = tn2 * tl.x + ty2 * tl.y - distCN2;
            // eslint-disable-next-line camelcase
            let distBN2 = tn2 * tr.x + ty2 * tr.y - distCN2;

            // eslint-disable-next-line camelcase
            if (distAN2 * distBN2 >= 0) {
                return { x: -1, y: -1 };
            }
            let distAN1 = tn1 * tl.x + ty1 * tl.y;
            let distCN1 = tn1 * bl.x + ty1 * bl.y - distAN1;
            let distDN1 = tn1 * br.x + ty1 * br.y - distAN1;
            if (distCN1 * distDN1 >= 0) {
                return { x: -1, y: -1 };
            }
            // 计算交点坐标
            let fraction = distAN2 / denominator;
            let dx = fraction * ty1;
            let dy = -fraction * tn1;
            return { x: tl.x + dx, y: tl.y + dy };
        },

        // 调用此方法可得到最终所有标注结果点位
        getAllObjects() {
            // 再更新一次画布 忘了有没有意义 管他呢
            this.fabricCanvas.requestRenderAll();

            // 获得所有Object
            let all = this.fabricCanvas.getObjects() || [];

            if (all.length) {
                // 仅获得标注 排除无关元素和仅用来展示的文字
                all = all.filter((x) => (x.objectId || x.objectId === 0) && x.tp !== 'text');

                all = all.map((x) => {
                    let obj = {
                        id: x.objectId,
                    };

                    // farbic.js 不支持获取移动或者缩放后的点位标注的坐标，做了处理
                    if (x.tp === 'polygn') {
                        obj.type = 2;
                        let minXIndex = 0;
                        let minYIndex = 0;
                        let tempMinX = 0;
                        let tempMinY = 0;
                        let tempIndex = -1;

                        let points = x.points.map((item) => {
                            tempIndex++;
                            if (!tempMinX) {
                                tempMinX = item.x;
                            }
                            if (!tempMinY) {
                                tempMinY = item.y;
                            }

                            if (item.x < tempMinX) {
                                tempMinX = item.x;
                                minXIndex = tempIndex;
                            }

                            if (item.y < tempMinY) {
                                tempMinY = item.y;
                                minYIndex = tempIndex;
                            }

                            item.x *= x.scaleX;
                            item.y *= x.scaleY;
                            return item;
                        });

                        let offsetX = points[minXIndex].x - x.left;
                        let offsetY = points[minYIndex].y - x.top;

                        for (let i = 0; i < points.length; i++) {
                            if (i === minXIndex) {
                                points[i].x = x.left;
                            } else {
                                points[i].x = points[i].x - offsetX;
                            }
                            if (i === minYIndex) {
                                points[i].y = x.top;
                            } else {
                                points[i].y = points[i].y - offsetY;
                            }
                        }

                        points = x.points.map((item) => {
                            item.x /= this.adapterRatio;
                            item.y /= this.adapterRatio;

                            item.x = item.x.toFixed(8);
                            item.y = item.y.toFixed(8);
                            return item;
                        });

                        obj.pointJson = JSON.stringify(points);
                    } else {
                        obj.width = ((x.width / this.adapterRatio) * x.scaleX).toFixed(8);
                        obj.height = ((x.height / this.adapterRatio) * x.scaleY).toFixed(8);
                        obj.sx1 = (x.lineCoords.tl.x / this.adapterRatio).toFixed(8);
                        obj.sy1 = (x.lineCoords.tl.y / this.adapterRatio).toFixed(8);
                        obj.sx2 = (x.lineCoords.br.x / this.adapterRatio).toFixed(8);
                        obj.sy2 = (x.lineCoords.br.y / this.adapterRatio).toFixed(8);
                        obj.type = 1;
                    }

                    return obj;
                });
            }
            return all;
        },

        toImage() {
            // 第一个参数是需要生成截图的元素,第二个是自己需要配置的参数,宽高等 getDataURL
            let dataURL = this.fabricCanvas.toDataURL({
                format: 'png',
            });
            return dataURL;
        },

        // 组件销毁时释放 URL
        cleanupBlobUrl() {
            if (this.imageBlobUrl) {
                URL.revokeObjectURL(this.imageBlobUrl);
                this.imageBlobUrl = null;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.canvas-main {
    width: 100%;
    height: 100%;
}
</style>
